<template>
  <settings-form :breadcrumbs="useSettingsBreadcrumbs()">
    <template #actions>
      <v-btn
        class="mr-2"
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>{{ $t('button.save_changes') }}
      </v-btn>
    </template>
    <template #content>
      <validation-observer ref="observerRef">
        <v-form
          ref="form"
          lazy-validation
        >
          <settings-group
            :title="$t('title.table_allocation_settings')"
            :subtitle="$t('label.description_table_allocations')"
            gutter
          >
            <v-col cols="12">
              <v-switch
                v-model="a.tableAllocation"
                :label="$t('label.table_allocation')"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="a.allocateWithEmptySeats"
                :items="seatItems"
                :label="$t('label.allow_empty_seats')"
              />
            </v-col>
          </settings-group>

          <settings-group
            :title="$t('label.general_options')"
            :subtitle="$t('settings.advanced.general_subtitle')"
            gutter
          >
            <v-col cols="12">
              <v-switch
                v-model="a.employeeNameIsNeeded"
                :label="$t('label.employee_name_needed')"
              />
            </v-col>
          </settings-group>

          <settings-group
            :title="$t('label.unread_reservation_options')"
            :subtitle="$t('settings.advanced.unread_options_subtitle')"
            gutter
          >
            <v-col cols="6">
              <v-switch
                v-model="a.markCancelledAsRead"
                :label="$t('label.mark_cancelled_reservations_as_read')"
              />
            </v-col>
            <v-col cols="6">
              <v-switch
                v-model="a.markAlwaysAsRead"
                :label="$t('label.mark_all_reservations_as_read')"
              />
            </v-col>
          </settings-group>

          <settings-group
            :title="$t('label.lunch_dinner_options')"
            :subtitle="$t('settings.advanced.lunch_dinner_subtitle')"
            gutter
          >
            <v-col cols="12">
              <v-switch
                v-model="a.showLunchDinner"
                :label="$t('label.show_lunch_dinner_counter')"
              />
            </v-col>
            <v-col cols="6">
              <time-picker-field
                v-model="a.splitLunchDinnerTime"
                :disabled="!a.showLunchDinner"
                :label="$t('label.cutoff_between_lunch_dinner')"
              />
            </v-col>
          </settings-group>

          <settings-group
            :title="$t('label.dark_mode_options')"
            :subtitle="$t('settings.advanced.dark_mode_subtitle')"
            gutter
            hide-divider
          >
            <v-col cols="12">
              <v-switch
                v-model="a.autoDarkMode"
                :label="$t('label.show_app_in_dark_mode')"
              />
            </v-col>
            <v-col cols="6">
              <time-picker-field
                v-model="a.autoDarkModeEnd"
                :disabled="!a.autoDarkMode"
                :label="$t('label.switch_light_mode_at_specific_time')"
              />
            </v-col>
            <v-col cols="6">
              <time-picker-field
                v-model="a.autoDarkModeStart"
                :disabled="!a.autoDarkMode"
                :label="$t('label.switch_dark_mode_at_specific_time')"
              />
            </v-col>
          </settings-group>
        </v-form>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script setup lang="ts">
import SettingsGroup from '@/components/layouts/SettingsGroup.vue';
import { computed, ref } from 'vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import { isModelEqualToModel, cloneModel } from '@/model/model-utils';
import { performSaveAction } from '@/services/vue-utils';
import AccountSettings from '@/model/AccountSettings';
import store from '@/store/store';
import useUnsavedGuard from '@/composables/unsaved-guard';
import useStoreWatch from '@/composables/store-watch';
import { ValidationObserver } from 'vee-validate';
import { emptySeatItems } from '@/components/views/settings/helpers/settings-values';

const observerRef = ref(null as null | InstanceType<typeof ValidationObserver>);

const a = ref(new AccountSettings());
const olda = ref(new AccountSettings());
const seatItems = emptySeatItems();

//
const reset = () => {
  const acctSettings = store.state.settings.accountSettings;
  a.value = cloneModel(acctSettings);
  olda.value = acctSettings;
};
useStoreWatch(reset);

//
const isChanged = computed(() => !isModelEqualToModel(a.value, olda.value));
useUnsavedGuard(isChanged);

//
const save = async () => {
  const ok = await performSaveAction(
    observerRef.value,
    async () => store.dispatch('sendAccountSettings', { entity: a.value }),
  );

  if (ok) {
    reset();
  }
};

</script>
